import React from 'react';
import { PaymentMethodData, APPLE_PAY_REQUIRED } from './PaymentMethodConstants';
import { logError } from '../../utils/logger-utils';

export interface PaymentMethodWithAvailability {
  key: string;
  info: PaymentMethodData[string];
  isAvailable: boolean;
}

export const isApplePayAvailable = async (): Promise<boolean> => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    if (typeof (window as any).ApplePaySession !== 'function') {
      return false;
    }

    // For Safari 18+, prefer applePayCapabilities if available
    if ((window as any).ApplePaySession.applePayCapabilities) {
      return (window as any).ApplePaySession.canMakePayments();
    }

    return (window as any).ApplePaySession.canMakePayments();
  } catch (error) {
    logError(new Error(`Failed to check Apple Pay availability: ${error}`));
    return true;
  }
};

const checkAvailabilityByRequirement = async (requirement: string): Promise<boolean> => {
  if (requirement === APPLE_PAY_REQUIRED) {
    return await isApplePayAvailable();
  } else {
    logError(`Unknown payment method requirement: ${requirement}`);
    return true;
  }
};

export const isPaymentMethodAvailable = async (
  methodInfo: PaymentMethodData[string]
): Promise<boolean> => {
  if (!methodInfo?.visibilityRequirements) {
    return true;
  }

  return await checkAvailabilityByRequirement(methodInfo.visibilityRequirements);
};

const getDefaultPaymentMethodData = (method: string): PaymentMethodData[string] => {
  return {
    name: 'Online Payment',
    img: React.createElement('img', {
      src: 'https://bccdn.britishcouncil.org/dev/online-payment-icons/v2/card_payment.svg',
      alt: method,
    }),
  };
};

const createPaymentMethodWithAvailability = async (
  method: string,
  paymentMethodData: PaymentMethodData
): Promise<PaymentMethodWithAvailability> => {
  const normalizedMethod = method.toLowerCase();
  let info = paymentMethodData[normalizedMethod];

  if (!info) {
    info = getDefaultPaymentMethodData(method);
  }

  const isAvailable = await isPaymentMethodAvailable(info);

  return {
    key: method,
    info,
    isAvailable,
  };
};

const processPaymentMethodsAvailability = async (
  paymentMethods: string[],
  paymentMethodData: PaymentMethodData
): Promise<PaymentMethodWithAvailability[]> => {
  if (paymentMethods.length === 0) {
    return [];
  }

  if (!paymentMethodData || Object.keys(paymentMethodData).length === 0) {
    logError('Payment method info is required but not provided');
    return [];
  }

  try {
    const results = await Promise.all(
      paymentMethods.map((method) => createPaymentMethodWithAvailability(method, paymentMethodData))
    );

    return results;
  } catch (error) {
    logError(new Error(`Failed to process payment methods availability: ${error}`));
    return paymentMethods.map((method) => ({
      key: method,
      info: paymentMethodData[method.toLowerCase()] || { img: null, withBrandedLogo: false },
      isAvailable: false,
    }));
  }
};

export const sortPaymentMethodsByAvailability = (
  methodsWithAvailability: PaymentMethodWithAvailability[]
): PaymentMethodWithAvailability[] => {
  if (!Array.isArray(methodsWithAvailability)) {
    return [];
  }

  return [...methodsWithAvailability].sort((a, b) => {
    if (a.isAvailable && !b.isAvailable) return -1;
    if (!a.isAvailable && b.isAvailable) return 1;
    return 0;
  });
};

export const getProcessedPaymentMethods = async (
  paymentMethods: string[],
  paymentMethodData: PaymentMethodData
): Promise<PaymentMethodWithAvailability[]> => {
  const processedMethods = await processPaymentMethodsAvailability(
    paymentMethods,
    paymentMethodData
  );

  return sortPaymentMethodsByAvailability(processedMethods);
};
